import type {
	IncomingInvoiceApprovalDecisionSchema,
	IncomingInvoiceReviewFormSchema,
} from "@msuite/utils/schemas";

/** What the review form leaves to its caller when it is turned into a decision. */
interface ApprovalDecisionOptions {
	/** False when the organisation keeps no active cost centre — the allocation is then empty. */
	hasCostCenters: boolean;
	/** A default approver deciding ahead of the open chain, having confirmed it. */
	skipChain: boolean;
}

/**
 * The decision the approval route takes, from the review form: a rejection carries its reason
 * alone; an acceptance carries the cost allocation without the shares the form entered it by,
 * and the Kürzung only while it is switched on — null takes an earlier one off the invoice.
 *
 * @param values - The submitted review form.
 * @param options - Whether there are cost centres, and whether the chain is skipped.
 * @returns The request body.
 */
export function toApprovalDecision(
	values: IncomingInvoiceReviewFormSchema,
	{ hasCostCenters, skipChain }: ApprovalDecisionOptions,
): IncomingInvoiceApprovalDecisionSchema {
	if (values.state === "rejected") {
		return { state: "rejected", comment: values.comment, skipChain };
	}
	return {
		state: "approved",
		comment: values.comment,
		allocations: hasCostCenters
			? values.allocations.map(({ costCenterId, amount }) => ({ costCenterId, amount }))
			: [],
		reducedNetAmount: values.isReduced ? values.reducedNetAmount : null,
		reductionReason: values.isReduced ? values.reductionReason : null,
		skipChain,
	};
}
